import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { State } from '@vallift/ngl';
import { BaseComponent } from '@core/base/base-component';
import { WorkspaceAPIService } from '@core/api/workspace/services/workspace-api/workspaceAPI.service';
import { TokenStorageService } from '@core/services/token-storage.service';
import { WorkspaceCardComponent } from '../../layout/components/workspace-card/workspace-card.component';
import {
  navigateAfterLogin,
  switchToWorkspace,
} from '@core/utils/auth-routing.util';
import {
  groupWorkspacesByCompany,
  type WorkspaceCompanyGroup,
} from '@core/utils/workspace-groups.util';
import type {
  ListMyWorkspacesApiResponse,
  MyWorkspaceListItemResponse,
} from '@core/api/workspace/models';

@Component({
  selector: 'app-select-workspace-page',
  imports: [RouterLink, WorkspaceCardComponent],
  templateUrl: './select-workspace-page.html',
  styleUrl: './select-workspace-page.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectWorkspacePage extends BaseComponent implements OnInit {
  private readonly userAPIService = inject(WorkspaceAPIService);
  private readonly tokenStorage = inject(TokenStorageService);

  readonly workspacesState = new State<ListMyWorkspacesApiResponse>();
  readonly switchingWorkspaceId = signal<string | null>(null);
  readonly returnUrl = signal<string | undefined>(undefined);

  ngOnInit(): void {
    if (!this.tokenStorage.isAuthenticated()) {
      this.router.navigate(['/login']);
      return;
    }

    if (this.tokenStorage.hasWorkspaceSession()) {
      this.router.navigate(['/dashboard']);
      return;
    }

    const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');
    if (returnUrl?.startsWith('/') && !returnUrl.startsWith('//')) {
      this.returnUrl.set(returnUrl);
    }

    this.loadWorkspaces();
  }

  workspaceGroups(): WorkspaceCompanyGroup[] {
    return groupWorkspacesByCompany(
      this.workspacesState.response()?.data?.workspaces ?? [],
    );
  }

  async onWorkspaceSelected(workspace: MyWorkspaceListItemResponse): Promise<void> {
    if (this.switchingWorkspaceId()) {
      return;
    }

    this.switchingWorkspaceId.set(workspace.id);

    try {
      const authData = await switchToWorkspace(
        this.userAPIService,
        this.tokenStorage,
        workspace.id,
      );

      await navigateAfterLogin(this.router, {
        workspace: authData.workspace,
        returnUrl: this.returnUrl(),
      });
    } catch {
      this.toasterService.error('Could not open this workspace. Please try again.');
    } finally {
      this.switchingWorkspaceId.set(null);
    }
  }

  private loadWorkspaces(): void {
    this.executeRequest({
      state: this.workspacesState,
      request: this.userAPIService.listMyWorkspaces(),
      handleError: true,
      errorMessage: 'Failed to load workspaces.',
    });
  }
}
